import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Download } from "lucide-react";

const ENTITIES = ["", "zones", "branches", "departments", "designations", "users"];

export default function AuditLogPage() {
  const { can } = useAuth();
  const [data, setData] = useState({ total: 0, rows: [] });
  const [entity, setEntity] = useState("");
  const [page, setPage] = useState(0);
  const limit = 50;

  const load = useCallback(() => {
    const params = { limit, skip: page * limit };
    if (entity) params.entity = entity;
    api.get("/audit", { params }).then(({ data }) => setData(data)).catch((e) => toast.error(apiError(e)));
  }, [entity, page]);
  useEffect(load, [load]);

  const exportCsv = async () => {
    try {
      const res = await api.get("/audit/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "audit_log.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const fieldLabel = (f) =>
    f === "__created__" ? <span className="text-[#1B7F3B] font-medium">Created</span> :
    f === "__deleted__" ? <span className="text-[#BB162B] font-medium">Deleted</span> : f;

  return (
    <div data-testid="audit-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="font-serif text-2xl sm:text-3xl text-[#0E2240] tracking-tight">Audit Log</h1>
          <p className="text-sm text-[#6B7280] mt-1">Immutable trail of every write. No entries can ever be deleted.</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            data-testid="audit-entity-filter"
            className="border border-gray-300 rounded-sm px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#BF9B30]"
            value={entity}
            onChange={(e) => { setEntity(e.target.value); setPage(0); }}
          >
            {ENTITIES.map((e) => <option key={e} value={e}>{e || "All entities"}</option>)}
          </select>
          {can("audit", "export") && (
            <button
              data-testid="audit-export-button"
              onClick={exportCsv}
              className="flex items-center gap-2 bg-[#0E2240] text-white text-sm px-4 py-2 rounded-sm hover:bg-[#1a3358] transition-colors duration-200"
            >
              <Download size={15} /> Export CSV
            </button>
          )}
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="audit-table">
            <thead>
              <tr className="border-b border-gray-200 bg-[#F5F6F8]">
                {["Timestamp", "Entity", "Entity ID", "Field", "Old value", "New value", "Actor", "Role", "IP"].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.rows.length === 0 ? (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-[#6B7280]">No audit entries</td></tr>
              ) : data.rows.map((r) => (
                <tr key={r.audit_id} className="border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors duration-200">
                  <td className="px-4 py-2.5 whitespace-nowrap tabular-nums text-xs">{new Date(r.timestamp).toLocaleString("en-IN")}</td>
                  <td className="px-4 py-2.5">{r.entity}</td>
                  <td className="px-4 py-2.5 text-xs text-[#6B7280]">{r.entity_id}</td>
                  <td className="px-4 py-2.5">{fieldLabel(r.field)}</td>
                  <td className="px-4 py-2.5 max-w-[180px] truncate text-[#6B7280]">{r.old_value ?? "—"}</td>
                  <td className="px-4 py-2.5 max-w-[180px] truncate">{r.new_value ?? "—"}</td>
                  <td className="px-4 py-2.5 text-xs">{r.actor_id}</td>
                  <td className="px-4 py-2.5 text-xs">{r.actor_role}</td>
                  <td className="px-4 py-2.5 text-xs text-[#6B7280]">{r.ip_address || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2.5 border-t border-gray-200 flex items-center justify-between text-xs text-[#6B7280]">
          <span className="tabular-nums">{data.total} entries</span>
          <div className="flex gap-2">
            <button data-testid="audit-prev-page" disabled={page === 0} onClick={() => setPage((p) => p - 1)}
              className="px-3 py-1 border border-gray-300 rounded-sm disabled:opacity-40 hover:bg-gray-50 transition-colors duration-200">Prev</button>
            <button data-testid="audit-next-page" disabled={(page + 1) * limit >= data.total} onClick={() => setPage((p) => p + 1)}
              className="px-3 py-1 border border-gray-300 rounded-sm disabled:opacity-40 hover:bg-gray-50 transition-colors duration-200">Next</button>
          </div>
        </div>
      </div>
    </div>
  );
}
